import { useNavigate, useRevalidator, useSearchParams } from "react-router";
import {
    Box,
    Container,
    Button,
    TextField,
    FormLabel,
    MenuItem,
    Autocomplete,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { type Control, Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { FormCompanySectorSelector } from "src/components/Selector/CompanySectorSelector";
import { useGetAllOrganization } from "src/api/client-api/company/useGetAllOrganization";
import { CompanyWrite, useCreateOrganization } from "src/api/client-api/company/useCreateOrganization";
import { useGetOrganizationTypes } from "src/api/client-api/useOrganizationType";
import { CompanySettingsForm, CompanyGridFormZodSchema } from "./CompanySettingsForm";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { Company } from "src/api/client-api/company/useGetOneOrganization.ts";
import { URLHandler } from "src/services/URLHandler";
import { JsonAttributesField } from "src/components/JsonAttributes/JsonAttributesField";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useGetCompanyPosts } from "src/api/client-api/post/useGetAllPosts";
import {
    ConfirmationModalData,
    DataDeletionWarningDialog,
} from "src/components/Dialog/Templates/DataDeletionWarningDialog";
import { useState } from "react";
import { SsqPostChangedWarnings } from "src/components/Dialog/Templates/AllWarnings/SsqPostChangedWarnings";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

type CompanyCreateProps = {
    isProvider?: boolean; // For suppliers
    company?: Company;
};

// Legacy entity tag interfaces removed

export const CompanyCreate = (props: CompanyCreateProps) => {
    const { company } = props;
    const navigate = useNavigate();
    const revalidator = useRevalidator();

    const [openConfirmationModal, setOpenConfirmationModal] = useState<ConfirmationModalData>({
        isOpen: false,
        errors: null,
    });

    const { search, delayedSearch, setSearch } = useDelayedSearch();
    const { companies = [] } = useGetAllOrganization({
        search: delayedSearch,
    });
    const { data: organizationTypes = [], isLoading: isLoadingOrgTypes } = useGetOrganizationTypes();
    const { entitiesJsonAttributes, currentCampaign } = useFoodpilotContext();

    const createCompany = useCreateOrganization();

    // Check if the company has existing SSQ posts (only when editing an existing company)
    const { posts: existingPosts } = useGetCompanyPosts({
        entityId: company?.id,
        status: "calculation",
        extraParams: {
            "campaign.id": currentCampaign?.id,
            itemsPerPage: 1, // We only need to know if any exist
        },
    });

    const [searchParams] = useSearchParams();

    const { t } = useTranslation();

    const fromProviders = !!searchParams.get("fromProviders");

    const organizationJsonAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.organization] || [];

    type Schema = z.infer<typeof CompanyGridFormZodSchema>;

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm<Schema>({
        mode: "onChange",
        resolver: zodResolver(CompanyGridFormZodSchema),
    });

    // Legacy entity tag logic removed

    const handleCompanyUpdate = (values: Schema) => {
        const companyWrite: CompanyWrite = CompanySettingsForm.getCompanyGridFormToCompanyWrite(values);
        createCompany.mutate(
            { company: companyWrite },
            {
                onSuccess: async (response) => {
                    revalidator.revalidate();
                    navigate(
                        fromProviders ? URLHandler.settings.providers.list() : URLHandler.settings.companies.list(),
                    );
                },
            },
        );
    };

    const onSubmitHandler: SubmitHandler<Schema> = (values) => {
        // Only show SSQ deletion warning when editing an existing company AND actually changing the sector AND there are existing posts
        const isEditingExistingCompany = company !== undefined;
        const currentSector = company?.organizationSector?.["@id"];
        const newSector = values.organizationSector;
        const isSectorActuallyChanging =
            isEditingExistingCompany && currentSector !== newSector && newSector !== undefined && newSector !== "";
        const hasExistingPosts = existingPosts && existingPosts.length > 0;

        if (isSectorActuallyChanging && hasExistingPosts) {
            setOpenConfirmationModal({
                isOpen: true,
                errors: <SsqPostChangedWarnings />,
            });
            return;
        }
        handleCompanyUpdate(values);
    };

    const companySectorId = company?.organizationSector?.["@id"];

    // Show loading if organization types are still loading
    if (isLoadingOrgTypes) {
        return <div>Loading organization types...</div>;
    }

    // Get default organization type
    const defaultOrgType = organizationTypes.find((orgType) => orgType.isDefault);

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <DataDeletionWarningDialog
                confirmationData={openConfirmationModal}
                setConfirmationData={setOpenConfirmationModal}
                onConfirm={() => {
                    const values = getValues();
                    handleCompanyUpdate(values);
                }}
            />
            <h1>{t("entreprise")}</h1>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: "1rem",
                    }}
                >
                    <Controller
                        name="isProvider"
                        control={control}
                        defaultValue={company?.isProvider || props.isProvider || false}
                        render={({ field: { ref, ...field } }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        ref={ref}
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    />
                                }
                                label="Is this company a supplier?"
                            />
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: "1rem",
                    }}
                >
                    <FormLabel>{"Organization Type"}</FormLabel>
                    <Controller
                        name="organizationType"
                        control={control}
                        defaultValue={
                            company?.organizationType?.id?.toString() || defaultOrgType?.id?.toString() || "1" // Default to "Company" ID
                        }
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                select
                                variant="outlined"
                                inputRef={ref}
                                error={Boolean(errors.organizationType)}
                                {...field}
                            >
                                {organizationTypes.map((orgType) => {
                                    return (
                                        <MenuItem key={orgType.id} value={orgType.id.toString()}>
                                            {orgType.name}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "rows",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "1rem",
                        marginBottom: "1rem",
                    }}
                >
                    <Controller
                        name="id"
                        control={control}
                        defaultValue={company?.id}
                        render={({ field: { ref, ...field } }) => <input type="hidden" {...field}></input>}
                    />
                    <Controller
                        name="name"
                        control={control}
                        defaultValue={company?.name}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                sx={{ width: "49%" }}
                                label={t("nom_de_l_entreprise")}
                                type="text"
                                variant="outlined"
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "rows",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "1rem",
                        marginBottom: "1rem",
                    }}
                >
                    <Controller
                        name="address1"
                        control={control}
                        defaultValue={company?.address1}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                sx={{ width: "49%" }}
                                label={t("adresse_1")}
                                type="text"
                                variant="outlined"
                                error={Boolean(errors.address1)}
                                helperText={errors.address1?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                    <Controller
                        name="address2"
                        control={control}
                        defaultValue={company?.address2}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                sx={{ width: "49%" }}
                                label={t("adresse_2")}
                                type="text"
                                variant="outlined"
                                error={Boolean(errors.address2)}
                                helperText={errors.address2?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "rows",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "1rem",
                        marginBottom: "1rem",
                    }}
                >
                    <Controller
                        name="city"
                        control={control}
                        defaultValue={company?.city}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                sx={{ width: "49%" }}
                                label={t("ville")}
                                type="text"
                                variant="outlined"
                                error={Boolean(errors.city)}
                                helperText={errors.city?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                    <Controller
                        name="state"
                        control={control}
                        defaultValue={company?.state}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                sx={{ width: "49%" }}
                                label={t("etat")}
                                type="text"
                                variant="outlined"
                                error={Boolean(errors.state)}
                                helperText={errors.state?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "rows",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "1rem",
                        marginBottom: "1rem",
                    }}
                >
                    <Controller
                        name="country"
                        control={control}
                        defaultValue={company?.country}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                sx={{ width: "49%" }}
                                label={t("pays")}
                                type="text"
                                variant="outlined"
                                error={Boolean(errors.country)}
                                helperText={errors.country?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                    <Controller
                        name="phone"
                        control={control}
                        defaultValue={company?.phone}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                sx={{ width: "49%" }}
                                label={t("telephone")}
                                type="tel"
                                variant="outlined"
                                error={Boolean(errors.phone)}
                                helperText={errors.phone?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: "1rem",
                    }}
                >
                    <Controller
                        name="email"
                        control={control}
                        defaultValue={company?.email}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                label={t("email")}
                                type="mail"
                                variant="outlined"
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: "1rem",
                    }}
                >
                    <FormLabel>{t("activite_de_l_entreprise")}</FormLabel>
                    <Controller
                        name="organizationSector"
                        control={control}
                        defaultValue={companySectorId || ""}
                        render={({ field: { value, onChange } }) => (
                            <FormCompanySectorSelector value={value || ""} onChange={onChange} />
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <FormLabel>{t("entreprises_parentes")}</FormLabel>
                    <Controller
                        name="parents"
                        control={control}
                        defaultValue={
                            Array.isArray(company?.parents) ?
                                company.parents.map((p) => p?.["@id"] ?? p?.id).filter(Boolean)
                            :   []
                        }
                        render={({ field: { onChange, value } }) => {
                            const normalizedCompanies = companies.map((c) => ({
                                ...c,
                                id: String(c.id),
                                "@id": c["@id"] || `/api/organizations/${c.id}`,
                            }));
                            const selectedCompanies = normalizedCompanies.filter((c) =>
                                (value ?? []).includes(c["@id"]),
                            );
                            return (
                                <Autocomplete
                                    multiple
                                    options={normalizedCompanies}
                                    value={selectedCompanies}
                                    onChange={(_, newValue) => {
                                        onChange(newValue.map((c) => c["@id"]));
                                    }}
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(option, value) => option["@id"] === value["@id"]}
                                    filterSelectedOptions
                                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                                />
                            );
                        }}
                    />
                </Box>
                <Box sx={{ marginBottom: "1rem" }}>
                    <JsonAttributesField
                        control={control as Control<any>}
                        jsonAttributes={organizationJsonAttributes}
                    />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Button sx={{ marginTop: "1rem", width: "10rem" }} variant="primary" type="submit">
                        {t("enregistrer")}
                    </Button>
                </Box>
            </form>
        </Container>
    );
};
